/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateSessionSchema,
  SESSION_TYPES,
  type CreateSessionInput,
} from '@therapy/shared-validators';
import { type SyntheticEvent, useCallback, type JSX } from 'react';
import { type UseFormRegisterReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '../../components/form/form-field.js';
import { Button } from '../../components/ui/button.js';
import { useClients, useTherapists } from '../../lib/queries/clinical.js';
import { useRooms } from '../../lib/queries/rooms.js';
import { useCreateSession } from '../../lib/queries/sessions.js';
import { useTherapyTypes } from '../../lib/queries/therapy-types.js';
import { getApiErrorMessage } from '../../lib/utils/api-error.js';

interface SessionFormProps {
  readonly onDone: () => void;
  /** Therapist portal: the session is implicitly the caller's, so hide the
   *  therapist picker (the API self-assigns it). */
  readonly hideTherapist?: boolean;
}

const SELECT_CLASS =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';
const LABEL_CLASS = 'mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300';

/** The session delivery-mode select (BRD §6 types); the empty option maps to undefined. */
function SessionTypeField({
  registration,
  error,
}: {
  readonly registration: UseFormRegisterReturn;
  readonly error?: string | undefined;
}): JSX.Element {
  const { t } = useTranslation();
  const types = useTherapyTypes(true);
  const names =
    types.data !== undefined && types.data.length > 0
      ? types.data.map((tt) => tt.name)
      : [...SESSION_TYPES];
  return (
    <div>
      <label htmlFor="session-type" className={LABEL_CLASS}>
        {t('form.session.type')}
      </label>
      <select
        id="session-type"
        className={SELECT_CLASS}
        defaultValue=""
        aria-invalid={error !== undefined}
        {...registration}
      >
        <option value="">{t('form.session.anyType')}</option>
        {names.map((st) => (
          <option key={st} value={st}>
            {st}
          </option>
        ))}
      </select>
      {error !== undefined && (
        <p role="alert" className="mt-1 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

/** Room select (Figma "Schedule Session" → Room); empty option maps to undefined. */
function RoomField({
  registration,
}: {
  readonly registration: UseFormRegisterReturn;
}): JSX.Element {
  const { t } = useTranslation();
  const rooms = useRooms(true);
  return (
    <div>
      <label htmlFor="session-room" className={LABEL_CLASS}>
        {t('form.session.room')}
      </label>
      <select id="session-room" className={SELECT_CLASS} defaultValue="" {...registration}>
        <option value="">{t('form.session.anyRoom')}</option>
        {(rooms.data ?? []).map((r) => (
          <option key={r.id} value={r.id}>
            {r.name}
          </option>
        ))}
      </select>
    </div>
  );
}

/** "Schedule Session" form (Figma). Validated by the shared `CreateSessionSchema`. */
export function SessionForm({ onDone, hideTherapist = false }: SessionFormProps): JSX.Element {
  const { t } = useTranslation();
  const clients = useClients();
  const therapists = useTherapists();
  const create = useCreateSession();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreateSessionInput>({ resolver: zodResolver(CreateSessionSchema) });

  const submit = useCallback(
    async (values: CreateSessionInput): Promise<void> => {
      await create.mutateAsync({
        clientId: values.clientId,
        ...(values.therapistId !== undefined ? { therapistId: values.therapistId } : {}),
        ...(values.sessionType !== undefined ? { sessionType: values.sessionType } : {}),
        ...(values.roomId !== undefined ? { roomId: values.roomId } : {}),
        scheduledStart: new Date(values.scheduledStart).toISOString(),
      });
      onDone();
    },
    [create, onDone],
  );

  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  return (
    <form onSubmit={onFormSubmit} className="space-y-4" noValidate>
      <div>
        <label htmlFor="session-client" className={LABEL_CLASS}>
          {t('form.session.client')}
        </label>
        <select
          id="session-client"
          className={SELECT_CLASS}
          defaultValue=""
          aria-invalid={errors.clientId !== undefined}
          {...register('clientId')}
        >
          <option value="" disabled>
            {t('form.session.selectClient')}
          </option>
          {(clients.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.fullName}
            </option>
          ))}
        </select>
        {errors.clientId !== undefined && (
          <p role="alert" className="mt-1 text-xs text-red-600">
            {errors.clientId.message}
          </p>
        )}
      </div>

      {!hideTherapist && (
        <div>
          <label htmlFor="session-therapist" className={LABEL_CLASS}>
            {t('form.session.therapist')}
          </label>
          <select
            id="session-therapist"
            className={SELECT_CLASS}
            defaultValue=""
            {...register('therapistId')}
          >
            <option value="">{t('form.session.anyTherapist')}</option>
            {(therapists.data ?? []).map((th) => (
              <option key={th.id} value={th.id}>
                {th.fullName}
              </option>
            ))}
          </select>
        </div>
      )}

      <SessionTypeField
        registration={register('sessionType', {
          setValueAs: (v: string) => (v === '' ? undefined : v),
        })}
        error={errors.sessionType?.message}
      />
      <RoomField
        registration={register('roomId', {
          setValueAs: (v: string) => (v === '' ? undefined : v),
        })}
      />
      <FormField
        id="session-start"
        type="datetime-local"
        label={t('form.session.start')}
        autoComplete="off"
        error={errors.scheduledStart?.message}
        registration={register('scheduledStart')}
      />

      {create.isError && (
        <p role="alert" className="text-sm text-red-600">
          {getApiErrorMessage(create.error, t('common.saveError'))}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting || create.isPending}>
          {t('form.session.submit')}
        </Button>
      </div>
    </form>
  );
}
